import React, { useState } from 'react';
import { Pencil, Trash2, Upload } from 'lucide-react';
import { frontendDb as db } from '../lib/frontend-db';
import type { CustomSection } from '../lib/frontend-db';

interface Props {
  section?: CustomSection;
  onSubmit: (data: Partial<CustomSection>) => Promise<void>;
  onCancel?: () => void;
  onDelete?: (id: string) => Promise<void>;
}

const CustomSectionEditor: React.FC<Props> = ({
  section,
  onSubmit,
  onCancel,
  onDelete
}) => {
  const [title, setTitle] = useState(section?.title || '');
  const [content, setContent] = useState(section?.content || '');
  const [imageUrl, setImageUrl] = useState(section?.image_url || '');
  const [buttonText, setButtonText] = useState(section?.button_text || '');
  const [buttonUrl, setButtonUrl] = useState(section?.button_url || '');
  const [settings, setSettings] = useState(section?.settings || {});
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let finalImageUrl = imageUrl;

      if (selectedImage) {
        finalImageUrl = await db.storeImage(selectedImage);
      }

      const data: Partial<CustomSection> = {
        title,
        content,
        image_url: finalImageUrl || null, // Allow null for image_url
        button_text: buttonText || null,
        button_url: buttonUrl || null,
        settings
      };

      await onSubmit(data);
      
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700">Titel</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Inhalt</label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={6}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Bild (optional)
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="mt-1 block w-full"
        />
        {(previewUrl || section?.image_url) && (
          <div className="mt-2">
            <img
              src={previewUrl || section?.image_url}
              alt="Vorschau"
              className="w-full max-h-48 object-cover rounded"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Button Text (optional)</label>
          <input
            type="text"
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Button URL (optional)</label>
          <input
            type="url"
            value={buttonUrl}
            onChange={(e) => setButtonUrl(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Hintergrundfarbe</label>
          <input
            type="color"
            value={settings.background_color || '#ffffff'}
            onChange={(e) => setSettings({ ...settings, background_color: e.target.value })}
            className="mt-1 block w-full h-10"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Textfarbe</label>
          <input
            type="color"
            value={settings.text_color || '#000000'}
            onChange={(e) => setSettings({ ...settings, text_color: e.target.value })}
            className="mt-1 block w-full h-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Button Style</label>
          <select
            value={settings.button_style || 'primary'}
            onChange={(e) => setSettings({ ...settings, button_style: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
          >
            <option value="primary">Primary</option>
            <option value="secondary">Secondary</option>
            <option value="text">Text</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Bildposition</label>
          <select
            value={settings.image_position || 'top'}
            onChange={(e) => setSettings({ ...settings, image_position: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring focus:ring-green-200"
          >
            <option value="top">Oben</option>
            <option value="left">Links</option>
            <option value="right">Rechts</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        {section && onDelete && (
          <button
            type="button"
            onClick={() => onDelete(section.id)}
            className="px-4 py-2 text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-700"
          >
            Abbrechen
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="bg-green-700 text-white px-4 py-2 rounded hover:bg-green-800 disabled:opacity-50"
        >
          {loading ? 'Wird gespeichert...' : (section ? 'Aktualisieren' : 'Erstellen')}
        </button>
      </div>
    </form>
  );
};

export default CustomSectionEditor;

